<script setup>
import { computed, watch } from 'vue';
import IEventItem from '@components/IEventItem/IEventItem.vue';
import ITeams from '@components/ITeamsMain/ITeamsMain.vue';
import IButton from '@components/IButton/IButton.vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import useSiteStore from '@vueApp/store/modules/site.js';
import EventsItemsEmpty from './EventsItemsEmpty.vue';
import EventItemsSkeleton from './EventItemsSkeleton.vue';

const store = useSiteStore();

const NEW_SITE_SALE_LEAGUE_MARKERS = ['ВХЛ', 'МХЛ'];
const newSiteSaleRedirect = window.NEW_SITE_LEAGUES_REDIRECT || {};

function isNewSiteSaleLeague(nomName) {
  return NEW_SITE_SALE_LEAGUE_MARKERS.some((marker) => nomName?.includes(marker));
}

function getNewSiteLink(event) {
  if (!newSiteSaleRedirect.enabled || !isNewSiteSaleLeague(event.nom?.name)) {
    return null;
  }
  try {
    const url = new URL(newSiteSaleRedirect.url, window.location.origin);
    url.searchParams.set('sittingId', event.id);
    return url.toString();
  } catch (e) {
    return null;
  }
}

function getEventLink(event) {
  return getNewSiteLink(event) || event.pageLink;
}

const loadingData = computed(
  () => store.getSubscriptionsState.loading
    || store.getEventsState.loading
    || store.getServicesState.loading
    || store.getPerformancesState.loading,
);

const showEvents = computed(
  () => !!store.getEventsState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(1)),
);

watch(loadingData, () => (loadingData.value ? $('#js-overlay').show() : $('#js-overlay').hide()));
</script>

<template>
  <event-items-skeleton :is-data-loading="loadingData">
    <events-items-empty>
      <div v-if="showEvents" class="mb-5">
        <span class="event-item__title">
          {{ translator.t("main", "Matches") }}
        </span>
        <i-event-item
          v-for="event in store.getEventsState.items"
          :key="event.id"
          :event="event"
          :is-package="event.eventType === 'Пакет'"
          :link-override="getNewSiteLink(event)"
          class="mb-3"
        >
          <template v-if="event.eventType !== 'Пакет'" #name>
            <i-teams
              :first-team="event.participants.team?.homeTeam || event.participants.singular"
              :second-team="event.participants.team?.guestTeam"
              :to="!event.settings.isSoon ? getEventLink(event) : ''"
              :is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent"
              :is-home-logo-without-opponent="event.settings.isHomeLogoWithoutOpponent"
              :is-show-tournament="event.settings.isShowTournament"
              :name="event.name"
              class="teams_main"
            />
          </template>
          <template v-else #package>
            <i-event-item
              v-for="eventItem in event.packageEvents"
              :key="eventItem.id"
              :event="eventItem"
              :is-package="eventItem.eventType === 'Пакет'"
              :is-package-item="true"
              :link-override="getNewSiteLink(eventItem)"
            >
              <template v-if="eventItem.eventType !== 'Пакет'" #name>
                <i-teams
                  :first-team="
                    eventItem.participants.team?.homeTeam || eventItem.participants.singular
                  "
                  :second-team="eventItem.participants.team?.guestTeam"
                  :to="!eventItem.settings.isSoon ? getEventLink(eventItem) : ''"
                  :is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent"
                  :is-home-logo-without-opponent="event.settings.isHomeLogoWithoutOpponent"
                  :is-show-tournament="event.settings.isShowTournament"
                  :name="event.name"
                  class="teams_main"
                />
              </template>
            </i-event-item>
          </template>
        </i-event-item>
        <div v-if="store.getEventsState.showLoadMore" class="w-100 d-flex justify-content-center">
          <i-button
            type="button"
            variant="primary-outlined"
            :on-click="store.loadMoreEvents"
            :loading="store.getEventsState.loadingMore"
          >
            {{ translator.t("main", "More") }}
          </i-button>
        </div>
      </div>
    </events-items-empty>
  </event-items-skeleton>
</template>

<style lang="scss" scoped>
.event-item {
  &__title {
    position: relative;
    font-weight: 700;
    font-size: 30px;
    color: $dark-text;
    margin-bottom: 24px;
    margin-top: 48px;
    display: block;
  }

  &__title_small {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 3px;
    font-weight: 700;
    font-size: 36px;
    color: rgba(0, 11, 77, 1);
  }

  @media (max-width: $screen-xs) {
    &__title {
      font-weight: 800;
      font-size: 24px;
      margin-bottom: 16px;
      margin-top: 0;
    }

    &__title_small {
      content: '';
      bottom: 3px;
      left: 2px;
      font-weight: 600;
      font-size: 20px;
    }
  }
}
</style>
